<template>
  <div
    v-if="hasAvailableAttributes"
    class="mb-3 flex flex-col space-y-3"
  >
    <div v-if="attribute1Options.length > 0">
      <label class="mb-1 block text-sm font-medium text-gray-700">
        {{ attributeLabels.attribute1 }}
      </label>
      <lokal-single-selector
        v-model="selectedAttribute1Record"
        :options="attribute1OptionRecords"
        :options-label="`Seleccionar ${attributeLabels.attribute1.toLowerCase()}`"
        option-label="label"
        key-option="label"
        size="md"
        @update:model-value="handleAttribute1Change"
      />
    </div>
    <div v-if="attribute2Options.length > 0">
      <label class="mb-1 block text-sm font-medium text-gray-700">
        {{ attributeLabels.attribute2 }}
      </label>
      <lokal-single-selector
        v-model="selectedAttribute2Record"
        :options="attribute2OptionRecords"
        :options-label="`Seleccionar ${attributeLabels.attribute2.toLowerCase()}`"
        option-label="label"
        key-option="label"
        size="md"
        @update:model-value="handleAttribute2Change"
      />
    </div>
    <div v-if="attribute3Options.length > 0">
      <label class="mb-1 block text-sm font-medium text-gray-700">
        {{ attributeLabels.attribute3 }}
      </label>
      <lokal-single-selector
        v-model="selectedAttribute3Record"
        :options="attribute3OptionRecords"
        :options-label="`Seleccionar ${attributeLabels.attribute3.toLowerCase()}`"
        option-label="label"
        key-option="label"
        size="md"
        @update:model-value="handleAttribute3Change"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import lokalSingleSelector, { type OptionRecord } from './shared/lokal-single-selector.vue';

const ATTRIBUTE_TYPE_LABELS: Record<string, string> = {
  size: 'Talla',
  color: 'Color',
  model: 'Modelo',
};

const ATTRIBUTES_NUMBER = 3;

const props = defineProps<{
  productGroup: ProductGroup | null | undefined;
  availableProducts: Product[];
  initialProduct: Product;
}>();

const emit = defineEmits<{
  'product-selected': [product: Product];
}>();

const selectedAttributes = ref({
  attribute1: props.initialProduct.attribute1 || '',
  attribute2: props.initialProduct.attribute2 || '',
  attribute3: props.initialProduct.attribute3 || '',
});

const attributeLabels = computed(() => ({
  attribute1:
    props.productGroup?.attribute1Type && props.productGroup.attribute1Type !== 'not_set'
      ? ATTRIBUTE_TYPE_LABELS[props.productGroup.attribute1Type] || 'Atributo 1'
      : '',
  attribute2:
    props.productGroup?.attribute2Type && props.productGroup.attribute2Type !== 'not_set'
      ? ATTRIBUTE_TYPE_LABELS[props.productGroup.attribute2Type] || 'Atributo 2'
      : '',
  attribute3:
    props.productGroup?.attribute3Type && props.productGroup.attribute3Type !== 'not_set'
      ? ATTRIBUTE_TYPE_LABELS[props.productGroup.attribute3Type] || 'Atributo 3'
      : '',
}));

function getAttributeOptions(attributeNumber: number) {
  const attributeType = props.productGroup?.[`attribute${attributeNumber}Type` as keyof ProductGroup];
  if (!attributeType || attributeType === 'not_set') return [];

  const filteredProducts = props.availableProducts.filter((product) => {
    const otherAttributes = [...Array(ATTRIBUTES_NUMBER).keys()].filter((num) => num !== attributeNumber);

    return otherAttributes.every((otherAttribute) => {
      const selectedValue =
        selectedAttributes.value[`attribute${otherAttribute}` as keyof typeof selectedAttributes.value];
      const productValue = product[`attribute${otherAttribute}` as keyof Product];
      return !selectedValue || productValue === selectedValue;
    });
  });

  const options = filteredProducts
    .map((product) => product[`attribute${attributeNumber}` as keyof Product])
    .filter((attribute) => attribute && attribute.trim() !== '')
    .filter((attribute, index, array) => array.indexOf(attribute) === index)
    .sort();

  return options;
}

const attribute1Options = computed(() => getAttributeOptions(1));
const attribute2Options = computed(() => getAttributeOptions(2));
const attribute3Options = computed(() => getAttributeOptions(3));

const hasAvailableAttributes = computed(
  () => attribute1Options.value.length > 0 || attribute2Options.value.length > 0 || attribute3Options.value.length > 0,
);

const attribute1OptionRecords = computed(() => attribute1Options.value.map((option) => ({ label: option })));

const attribute2OptionRecords = computed(() => attribute2Options.value.map((option) => ({ label: option })));

const attribute3OptionRecords = computed(() => attribute3Options.value.map((option) => ({ label: option })));

const selectedAttribute1Record = ref({
  label: props.initialProduct.attribute1,
} as OptionRecord);
const selectedAttribute2Record = ref({
  label: props.initialProduct.attribute2,
} as OptionRecord);
const selectedAttribute3Record = ref({
  label: props.initialProduct.attribute3,
} as OptionRecord);

function findMatchingProduct() {
  const matchingProduct = props.availableProducts.find((product) => {
    const attribute1Match =
      !selectedAttributes.value.attribute1 || product.attribute1 === selectedAttributes.value.attribute1;
    const attribute2Match =
      !selectedAttributes.value.attribute2 || product.attribute2 === selectedAttributes.value.attribute2;
    const attribute3Match =
      !selectedAttributes.value.attribute3 || product.attribute3 === selectedAttributes.value.attribute3;

    return attribute1Match && attribute2Match && attribute3Match;
  });
  return matchingProduct || props.initialProduct;
}

function validateAndClearInvalidSelections() {
  if (selectedAttributes.value.attribute1 && !attribute1Options.value.includes(selectedAttributes.value.attribute1)) {
    selectedAttributes.value.attribute1 = '';
  }
  if (selectedAttributes.value.attribute2 && !attribute2Options.value.includes(selectedAttributes.value.attribute2)) {
    selectedAttributes.value.attribute2 = '';
  }
  if (selectedAttributes.value.attribute3 && !attribute3Options.value.includes(selectedAttributes.value.attribute3)) {
    selectedAttributes.value.attribute3 = '';
  }
}

function updateSelectedProduct() {
  validateAndClearInvalidSelections();
  const newProduct = findMatchingProduct();
  emit('product-selected', newProduct);
}

function handleAttribute1Change(optionRecord: OptionRecord) {
  selectedAttributes.value.attribute1 = (optionRecord.label as string) || '';
  updateSelectedProduct();
}

function handleAttribute2Change(optionRecord: OptionRecord) {
  selectedAttributes.value.attribute2 = (optionRecord.label as string) || '';
  updateSelectedProduct();
}

function handleAttribute3Change(optionRecord: OptionRecord) {
  selectedAttributes.value.attribute3 = (optionRecord.label as string) || '';
  updateSelectedProduct();
}

watch(
  () => selectedAttributes.value,
  () => {
    updateSelectedProduct();
  },
  { deep: true },
);

watch(
  () => props.initialProduct,
  (newProduct) => {
    if (newProduct.attribute1 && !selectedAttributes.value.attribute1) {
      selectedAttributes.value.attribute1 = newProduct.attribute1;
    }
    if (newProduct.attribute2 && !selectedAttributes.value.attribute2) {
      selectedAttributes.value.attribute2 = newProduct.attribute2;
    }
    if (newProduct.attribute3 && !selectedAttributes.value.attribute3) {
      selectedAttributes.value.attribute3 = newProduct.attribute3;
    }
  },
  { immediate: true },
);
</script>
